import { Pagination } from "../pagination.interface";
import { AttachmentInterface, Attachment } from "../attachments/api.models";

interface MessageCollectionInterface {
  id: number;
  state: string;
  languageId: number;
  behaviour: string;
  variables: object;
  metadata: MessageMetadataInterface;
  messages: MessageInterface[];
  createdBy: string;
  approvedBy: string;
  disapprovedBy: string;
  deletedBy: string;
}

interface ProfileInterface {
  relationshipStatus: string[];
  hasPhoto: boolean;
  ageGroup: any[];
}

interface MessageMetadataInterface {
  tags: string[];
  searchPreferences: SearchPreferences;
  timerange: any[];
  receiver: ProfileInterface;
  sender: ProfileInterface;
  daysOfWeek: any[];
  nearby: boolean;
}

interface MessageInterface {
  id: number;
  subject: string;
  content: string;
  type: string;
  created: string;
  updated: string;
  attachment: AttachmentInterface;
  attachmentId: number;
  attachmentType: string;
}

class MessageMetadataProfile implements ProfileInterface {
  ageGroup: any[] = [];
  hasPhoto: boolean = null;
  relationshipStatus: string[] = [];
}

class SearchPreferences {
  gender?: string = MessageCollection.GENDER_FEMALE;
}

class MessageMetadata implements MessageMetadataInterface {
  searchPreferences: SearchPreferences = new SearchPreferences();
  tags: string[] = [MessageCollection.TAG_DEFAULT];
  receiver: ProfileInterface = new MessageMetadataProfile();
  sender: ProfileInterface = new MessageMetadataProfile();
  timerange: any[] = [];
  daysOfWeek: any[] = [];
  nearby: boolean = null;
}

class MessageCollection implements MessageCollectionInterface {
  static readonly BEHAVIOUR_AGGRESSIVE = "aggressive";
  static readonly BEHAVIOUR_NOT_AGGRESSIVE = "not_aggressive";
  static readonly BEHAVIOUR_MODERATELY_AGGRESSIVE = "moderately_aggressive";

  static readonly STATUS_READY_FOR_APPROVAL = "ready_for_approval";
  static readonly STATUS_APPROVED = "approved";
  static readonly STATUS_DISAPPROVED = "disapproved";

  static readonly GENDER_FEMALE = "female";
  static readonly GENDER_SHEMALE = "shemale";

  static readonly RELATIONSHIP_SINGLE = "single";
  static readonly RELATIONSHIP_MARRIED = "married";
  static readonly RELATIONSHIP_IN_RELATIONSHIP = "relationship";
  static readonly RELATIONSHIP_WIDOW = "widow";

  static readonly TAG_DEFAULT = "default";
  static readonly TAG_NEW_PHOTO = "customer_has_new_photo";
  static readonly TAG_ONLINE = "customer_is_online";
  static readonly TAG_OFFLINE = "customer_is_offline";
  static readonly TAG_RESIDENCE_CHANGED = "customer_changed_residence";
  static readonly TAG_RECEIVED_PROPOSAL_ACCEPTED = "customer_accepted_proposal";
  static readonly TAG_SENT_PROPOSAL_ACCEPTED = "player_accepted_proposal";
  static readonly TAG_LEAD_2_SALE = "lead_2_sale";
  static readonly TAG_INSECURE = "insecure";
  static readonly TAG_PROFILE_VISIT = "profile_visit";
  static readonly TAG_PROFILE_ACTIVATION = "profile_activation";
  static readonly TAG_BUZZER = "buzzer";
  static readonly TAG_SOI_LIKE = "soi_like";
  static readonly TAG_STOPPED_CONVERSATION = "stopped_conversation";
  static readonly TAG_CUSTOMER_IS_ACTIVE = "customer_is_active";

  static readonly TAG_FLIRT_DEFAULT = "flirt_default";
  static readonly TAG_FLIRT_SHEMALE = "flirt_shemale";
  static readonly TAG_FLIRT_GIFT = "flirt_gift";
  static readonly TAG_FLIRT_WINK = "flirt_wink";
  static readonly TAG_FLIRT_IIYORU = "flirt_iiyoru";

  static readonly TAG_FAVORITE_DEFAULT = "favorite_default";
  static readonly TAG_FAVORITE_SHEMALE = "favorite_shemale";
  static readonly TAG_FAVORITE_SPEEDDIAL = "favorite_speeddial";
  static readonly TAG_FAVORITE_WISHLIST = "favorite_wishlist";
  static readonly TAG_FAVORITE_LIKE = "favorite_like";

  static readonly TAG_EVENT_MATCH = "event_match";

  static readonly TAG_SEXUAL_ATTRACTION = "sexual_attraction";

  static readonly TAG_ASK_PICTURE = "ask_picture";
  static readonly TAG_RE_ENGAGEMENT = "re_engagement";


  static readonly behaviours: string[] = [
    MessageCollection.BEHAVIOUR_NOT_AGGRESSIVE,
    MessageCollection.BEHAVIOUR_MODERATELY_AGGRESSIVE,
    MessageCollection.BEHAVIOUR_AGGRESSIVE,
  ];

  static readonly tags: string[] = [
    MessageCollection.TAG_DEFAULT,
    MessageCollection.TAG_LEAD_2_SALE,
    MessageCollection.TAG_INSECURE,
    MessageCollection.TAG_NEW_PHOTO,
    MessageCollection.TAG_ONLINE,
    MessageCollection.TAG_OFFLINE,
    MessageCollection.TAG_RESIDENCE_CHANGED,
    MessageCollection.TAG_RECEIVED_PROPOSAL_ACCEPTED,
    MessageCollection.TAG_SENT_PROPOSAL_ACCEPTED,
    MessageCollection.TAG_PROFILE_VISIT,
    MessageCollection.TAG_PROFILE_ACTIVATION,
    MessageCollection.TAG_BUZZER,
    MessageCollection.TAG_SOI_LIKE,
    MessageCollection.TAG_STOPPED_CONVERSATION,
    MessageCollection.TAG_CUSTOMER_IS_ACTIVE,
    MessageCollection.TAG_FLIRT_DEFAULT,
    MessageCollection.TAG_FLIRT_SHEMALE,
    MessageCollection.TAG_FLIRT_GIFT,
    MessageCollection.TAG_FLIRT_WINK,
    MessageCollection.TAG_FLIRT_IIYORU,
    MessageCollection.TAG_FAVORITE_DEFAULT,
    MessageCollection.TAG_FAVORITE_SHEMALE,
    MessageCollection.TAG_FAVORITE_SPEEDDIAL,
    MessageCollection.TAG_FAVORITE_WISHLIST,
    MessageCollection.TAG_FAVORITE_LIKE,
    MessageCollection.TAG_EVENT_MATCH,
    MessageCollection.TAG_SEXUAL_ATTRACTION,
    MessageCollection.TAG_ASK_PICTURE,
    MessageCollection.TAG_RE_ENGAGEMENT
  ];

  static readonly genders: string[] = [
    MessageCollection.GENDER_FEMALE,
    MessageCollection.GENDER_SHEMALE,
  ];

  static readonly statuses: string[] = [
    MessageCollection.STATUS_READY_FOR_APPROVAL,
    MessageCollection.STATUS_APPROVED,
    MessageCollection.STATUS_DISAPPROVED,
  ];

  static readonly relationships: string[] = [
    MessageCollection.RELATIONSHIP_IN_RELATIONSHIP,
    MessageCollection.RELATIONSHIP_MARRIED,
    MessageCollection.RELATIONSHIP_SINGLE,
    MessageCollection.RELATIONSHIP_WIDOW,
  ];

  static readonly variables: object = [
    {
      sender: [
        "fromName",
        "fromAge",
        "fromRealName",
        "playerCity",
        "playerProvince",
      ],
      receiver: [
        "hairColor",
        "eyeColor",
        "build",
        "education",
        "city",
        "province",
        "country",
        "name",
        "age",
        "gender",
      ],
      common: ["dayOfWeek"],
    },
  ];

  behaviour: string = MessageCollection.BEHAVIOUR_MODERATELY_AGGRESSIVE;
  variables: object = MessageCollection.variables;
  id: number;
  languageId: number = 0;
  messages: MessageInterface[] = [];
  metadata: MessageMetadataInterface = new MessageMetadata();
  approvedBy: string;
  createdBy: string;
  deletedBy: string;
  disapprovedBy: string;

  state: string = MessageCollection.STATUS_READY_FOR_APPROVAL;
}

class Message implements MessageInterface {
  static readonly TYPE_NORMAL = "normal";
  static readonly TYPE_FLIRT = "flirt";
  static readonly TYPE_FAVORITE = "favorite";
  static readonly TYPE_LIKE = "like";
  static readonly TYPE_PROPOSAL = "proposal";
  static readonly types: string[] = [
    Message.TYPE_NORMAL,
    Message.TYPE_FLIRT,
    Message.TYPE_FAVORITE,
    Message.TYPE_LIKE,
    Message.TYPE_PROPOSAL,
  ];

  public id: number = null;
  public created: string;
  public updated: string;
  public subject: string = null;
  public content: string = null;
  public type: string = Message.TYPE_NORMAL;
  public attachment: AttachmentInterface;
  public attachmentId: number;
  public attachmentType: string = Attachment.TYPE_NONE;
  public attachmentData: string;
}

class MessageCollectionRespone {
  result: MessageCollection[];
  pagination: Pagination;
}

// baseNameCollection: any > atm comes back as null
// messageCollection: any > atm comes back as null

class MessageStatistics {
  baseName: string;
  baseNameCollection: any;
  created: string;
  id: number;
  messageCollection: any;
  messageCollectionId: number;
  repliedPaid: number;
  sent: number;
  updated: string;
  type: string;
  tag: string;
  conversionRatio: number;
}

export {
  MessageCollectionRespone,
  MessageCollection,
  Message,
  MessageMetadata,
  MessageMetadataProfile,
  MessageStatistics,
};



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/shared/api/messages/api.models.ts